import {
  getAllCardsInPlay,
  getLastMatchingTurns,
  ObfuscatedGameState,
  Options,
  GameState,
  SwapType,
  getOppositeRankLabel,
  isPureSet
} from "@presidents/game-engine";
import type {
  Card as TCard,
  TurnPass,
  TurnTrick
} from "@presidents/game-engine/src/types";
import React, { useEffect, useState, PropsWithChildren } from "react";
import { unstable_batchedUpdates as batchedUpdates } from "react-dom";
import styled from "styled-components";
import { useDoubleTap } from "use-double-tap";
import { useSocket } from "../../context/SocketContext";
import { useUser } from "../../context/UserContext";
import { forPhoneOnly } from "../../theme/media-queries";
import { Button } from "../Button";
import { Toast } from "../ErrorMessage";
import MyHand from "./MyHand";
import useHasPlayedRevolution from "./useHasPlayedRevolution";

const MyHandOuter = styled.section`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--bottom-control-bar-height);

  background-color: hsla(var(--layer3HSL), 0.5);
  &[data-my-turn="true"] {
    background-color: var(--successMuted);
  }
  backdrop-filter: blur(4px);
`;
export const Controls = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: var(--bottom-control-bar-height);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const MyTurnControls = styled.div`
  white-space: nowrap;
  position: absolute;
  left: 50%;
  bottom: 100%;
  margin-bottom: 8px;
  transform: translate(-50%, 0);
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;

  & button + button {
    margin-left: 16px;
  }
`;

const PlayHandButton = styled(Button)`
  font-size: 24px;
  min-width: 200px;
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
  @media ${forPhoneOnly} {
    font-size: 16px;
  }
`;

const PassButton = styled(Button)`
  font-size: 24px;
  min-width: 100px;
  transform: translate(0);
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
  @media ${forPhoneOnly} {
    font-size: 16px;
  }
`;

const checkCanRevolution = (
  lastRun: TCard[][] | undefined,
  myCards: TCard[] | undefined
) => {
  if (!lastRun || lastRun.length === 0) {
    return false;
  }
  if (!isPureSet(lastRun[lastRun.length - 1])) {
    return false;
  }
  if (!myCards || myCards.length === 0) {
    return false;
  }
  const runLength = lastRun.reduce((count, cards) => {
    return (count += cards.length);
  }, 0);
  const value = lastRun.pop()![0].value;
  const myMatching = myCards.filter((c) => c.value === value);
  return myMatching.length + runLength === 4;
};

type Props = {
  roomId: string;
  gameOptions?: Options;
  gameState?: ObfuscatedGameState;
};
const MyHandAndControls: React.FC<PropsWithChildren<Props>> = ({
  roomId,
  gameOptions,
  gameState,
  children
}) => {
  // Meta
  const user = useUser()!;
  const activeGame = gameState?.state && gameState?.state !== GameState.FINISH;

  // Turn & hand
  const showMyHand = !!gameState?.myHand;
  const rank = gameState?.myHand?.rank;
  const myTurn =
    activeGame && gameState && !!user && gameState?.whosNext === user?.uid;
  const canPass = myTurn && gameState?.inPlay !== undefined;

  // Swap
  const shouldSwap = gameState?.state === GameState.SWAP;
  const hasSwapped =
    shouldSwap && gameState?.mySwaps?.some((s) => s.fromPlayer === user.uid);
  const numSwap = gameState?.myHand?.numSwap;
  const mustSwap = shouldSwap && !!numSwap;
  const swapType = gameState?.myHand?.swapType;

  const socket = useSocket((s) => {
    const errHandler = (
      message: string = "Unknown error occured. Please try again"
    ) => {
      console.error(message);
      batchedUpdates(() => {
        _setSelected([]);
        setTurnLoading(false);
        setToastMessage(message);
      });
    };
    s.on("error", errHandler);
    s.on("unauthorised", errHandler);
    return () => {
      s.off("error", errHandler);
      s.off("unauthorised", errHandler);
    };
  });
  const [turnLoading, setTurnLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [selected, _setSelected] = useState<number[]>([]);
  const setSelected = async (nextSelected: number[]) => {
    if (hasSwapped) {
      return;
    }
    // const res = await socket?.emitPromise("cardselected", {
    //   roomId,
    //   selected: nextSelected
    // });
    setToastMessage(null);
    _setSelected(nextSelected);
    socket?.emit("cardselected", {
      roomId,
      selected: nextSelected
    });
  };

  /**
   * Reset local state on remote state change
   */
  useEffect(() => {
    console.log("Reset local state on remote state change");
    setToastMessage(null);
    setTurnLoading(false);
  }, [gameState?._id]);

  /**
   * Reset selected on turn change
   */
  useEffect(() => {
    console.log("Reset selected on turn change");
    setSelected([]);
  }, [gameState?.whosNext, gameState?.state, gameState?.round]);

  /**
   * Set the toast if we mustSwap
   */
  useEffect(() => {
    if (mustSwap) {
      setToastMessage(
        `Swap ${numSwap} of your ${
          swapType === SwapType.high ? "highest" : "lowest"
        } card${numSwap > 1 ? "s" : ""} with the ${getOppositeRankLabel(rank)}`
      );
    }
  }, [mustSwap]);

  const triggerPass = () => {
    setTurnLoading(true);
    socket?.emit("turn", {
      roomId,
      turn: {
        player: user.uid,
        type: "pass",
        reason: "player"
      } as TurnPass
    });
  };
  const shouldDoubleTapPass =
    typeof window !== "undefined" && "ontouchstart" in window;
  const { onClick: onDoubleTapToPass } = useDoubleTap(triggerPass, 300, {
    onSingleTap: () => {
      setToastMessage("Double tap to Pass");
    }
  });
  const onClickPass = triggerPass;
  const onClickPlayCard = () => {
    if (!selected.length) {
      setToastMessage("Select a card first!");
      return;
    }
    setTurnLoading(true);
    const cards = selected.map((i) => {
      return gameState?.myHand?.cards[i];
    });
    if (mustSwap) {
      if (cards.length !== numSwap!) {
        setToastMessage(`You must select ${numSwap} to swap!`);
        setTurnLoading(false);
        return;
      }
      socket?.emit("swap", {
        roomId,
        swap: {
          fromPlayer: user.uid,
          cards
        }
      });
    } else {
      socket?.emit("turn", {
        roomId,
        turn: {
          player: user.uid,
          cards,
          type: "trick"
        } as TurnTrick
      });
    }
  };
  const hasPlayedRevolution = useHasPlayedRevolution();
  const allowRevolution = !!gameOptions?.rules?.fourOfAKindRevolution;
  const canRevolution =
    allowRevolution &&
    gameState?.turns !== undefined &&
    checkCanRevolution(
      getLastMatchingTurns(getAllCardsInPlay(gameState.turns)),
      gameState?.myHand?.cards
    );
  const showRevolutionButton =
    (canRevolution && !hasPlayedRevolution) ||
    (canRevolution && selected.length > 0);

  const getCTAText = () => {
    if (shouldSwap) {
      if (mustSwap && selected.length !== numSwap!) {
        return `Select your ${numSwap > 1 ? `${numSwap} ` : ""}${
          swapType === SwapType.high ? "highest" : "lowest"
        } card${numSwap > 1 ? "s" : ""} to swap`;
      } else if (hasSwapped) {
        return "Waiting on swaps...";
      }
      return "Swap Card" + (selected.length > 1 ? "s" : "");
    }
    if (canRevolution) {
      return "Play Revolution";
    }
    return "Play Card" + (selected.length > 1 ? "s" : "");
  };

  return (
    <MyHandOuter data-my-turn={myTurn}>
      {showMyHand && (
        <>
          <MyTurnControls>
            {myTurn && canPass && (
              <PassButton
                appearance="secondary"
                disabled={!myTurn || turnLoading}
                onClick={shouldDoubleTapPass ? onDoubleTapToPass : onClickPass}
              >
                Pass
              </PassButton>
            )}
            {(mustSwap || hasSwapped || myTurn || showRevolutionButton) && (
              <PlayHandButton
                disabled={turnLoading || hasSwapped}
                onClick={onClickPlayCard}
                isFancy={shouldSwap && !hasSwapped}
                appearance={hasSwapped ? "secondary" : "primary"}
              >
                {getCTAText()}
              </PlayHandButton>
            )}
          </MyTurnControls>
          <MyHand selected={selected} setSelected={setSelected} />
        </>
      )}
      {toastMessage && (
        <Toast onClick={() => setToastMessage(null)}>
          {typeof toastMessage === "string"
            ? toastMessage
            : (toastMessage as any).message ?? JSON.stringify(toastMessage)}
        </Toast>
      )}
      {children}
    </MyHandOuter>
  );
};

export default MyHandAndControls;
